/* eslint-disable @typescript-eslint/no-explicit-any */

import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useMutation } from "@apollo/client/react";
import {
  Autocomplete,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { FloppyDiskIcon } from "@phosphor-icons/react/dist/csr/FloppyDisk";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { ChangeEvent, FC, ReactNode, useEffect } from "react";
import { Controller, ControllerRenderProps, useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { SAVE_INTEGRATION_MUTATION } from "../../graphql";
import { Integration } from "../../graphql/types";
import { getObjectField, NestedObject, validateEmail } from "../../lib/Util";
import { FormComponent } from "../common/Form";
import { StatefulButton } from "../ui/muiButtons";
import { BaseIntegration } from "./BaseIntegration";

class TestIntegration extends BaseIntegration {
  displayName() {
    return getObjectField(this as unknown as NestedObject, "public_authorization.name") || "Test Integration";
  }

  errors() {
    const errorMessages: {
      message: string;
      path?: string;
    }[] = [];

    const opError = getObjectField(this as unknown as NestedObject, "configuration.op_error");
    if (opError) {
      errorMessages.push({
        message: opError.toString(),
        path: "configuration.op_error",
      });
    }

    const opFormError = getObjectField(this as unknown as NestedObject, "configuration.op_form_error");
    if (opFormError) {
      errorMessages.push({ message: opFormError.toString() });
    }

    const items = getObjectField(this as unknown as NestedObject, "configuration.items") || [];
    if (Array.isArray(items)) {
      items.forEach((item, index) => {
        if (item.error) {
          errorMessages.push({
            message: item.error,
            path: `configuration.items[${index}].fruit`,
          });
        }
      });
    }

    return errorMessages;
  }
}

const lettersExceptAI = (text: string): string | undefined => {
  const regex = /^[b-hj-z]+$/i;
  const match = regex.test(text);
  if (!match && text) {
    return "Use only letters, except A or I.";
  }
};

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const defaultValuesForm = {
  "authorization.name": "",
  "authorization.email": "",
  "authorization.password": "",
  "configuration.client_validated": "",
  "configuration.schema_validated": "",
  "configuration.denied": "",
  "configuration.semantically_validated": "",
  "configuration.form_error": "",
  "configuration.op_error": "",
  "configuration.op_form_error": "",
  "configuration.typeahead": "",
  filters: [],
  "configuration.items": [],
};

const removeEmptyFields = (data: any) => {
  const result = data;
  Object.keys(data.configuration).forEach((key) => {
    if (data.configuration[key] === "" || data.configuration[key] == null) {
      delete result.configuration[key];
    }
  });
  return result;
};

const ArrayItems: FC<{ fields: any[]; children: (props: any) => ReactNode }> = ({ fields, children }) => {
  return fields.map((name, index) => {
    return children({ items: fields, name, index });
  });
};

interface ITestIntegrationEditor {
  integration: Integration;
}

const TestIntegrationEditor: FC<ITestIntegrationEditor> = ({ integration }) => {
  const { control, handleSubmit, getValues, formState, setError, setValue } = useForm<any>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: defaultValuesForm,
  });

  const onError = (error: Error) => {
    toast.error("The integration settings were not saved, because there are errors to fix.");
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach((gqlError) => {
        const jsonPath = gqlError.extensions?.jsonPath as string | undefined;
        if (jsonPath) {
          setError(jsonPath, { message: gqlError.message });
        }
      });
    }
  };

  const onCompleted = () => {
    toast.success("The integration settings have been saved.");
  };
  const [saveIntegration, { loading }] = useMutation(SAVE_INTEGRATION_MUTATION, {
    refetchQueries: ["list_integrations"],
    onCompleted: onCompleted,
    onError,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "filters",
  });

  const {
    fields: itemsField,
    append: itemsAppend,
    remove: itemsRemove,
  } = useFieldArray({
    control,
    name: "configuration.items",
  });

  const onSubmit = (values: any) => {
    // We dont wanna send optional values as empty strings, so we need to remove them
    const cleanedData = removeEmptyFields(values);
    const { authorization, configuration, filters } = cleanedData;
    const payload = {
      variables: {
        id: integration.id,
        integration_type: integration.integration_type,
        authorization: authorization,
        configuration: configuration,
        filters: filters,
      },
    };
    saveIntegration(payload);
  };

  const validateButtonRules = (index: number) => {
    if (
      !getValues(`filters[${index}].production`) &&
      !getValues(`filters[${index}].backfills`) &&
      !getValues(`filters[${index}].lateUploads`) &&
      !getValues(`filters[${index}].proofs`) &&
      !getValues(`filters[${index}].beta`)
    )
      return "At least one filter must be selected";
  };
  const checkError = (index: number) => {
    if (formState.errors.filters) {
      return Boolean((formState.errors.filters as any)[index]?.production);
    }
    return false;
  };

  type TestIntegrationFormValues = {
    "authorization.name": string;
    "authorization.email": string;
    "authorization.password": string;
  };

  const onChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: ControllerRenderProps<TestIntegrationFormValues, keyof TestIntegrationFormValues>
  ) => {
    field.onChange(e);
    setValue(field.name, e.target.value, { shouldValidate: true });
  };

  useEffect(() => {
    const fillForm = () => {
      setValue("authorization.name", integration.public_authorization?.name);
      setValue("authorization.email", integration.public_authorization?.email);
      setValue("configuration.typeahead", integration.configuration?.typeahead);

      integration.filters?.forEach((filter, idx) => {
        append("filters");
        setValue(`filters[${idx}].configuration`, filter?.configuration.typeahead);
        setValue(`filters[${idx}].production`, filter?.production);
        setValue(`filters[${idx}].proofs`, filter?.proofs);
        setValue(`filters[${idx}].lateUploads`, filter?.lateUploads);
        setValue(`filters[${idx}].backfills`, filter?.backfills);
        setValue(`filters[${idx}].beta`, filter?.beta);
        setValue(`filters[${idx}].configuration.name`, filter?.configuration?.name);
      });

      integration.configuration?.items?.forEach((item: any, idx: number) => {
        itemsAppend("");
        setValue(`configuration.items[${idx}].fruit`, item?.fruit);
        setValue(`configuration.items[${idx}].color`, item?.color);
      });
      setValue("authorization.name", integration.public_authorization?.name);
      setValue(`configuration.client_validated`, integration?.configuration?.client_validated);
      setValue(`configuration.schema_validated`, integration?.configuration?.schema_validated);
      setValue(`configuration.denied`, integration?.configuration?.denied);
      setValue(`configuration.semantically_validated`, integration?.configuration?.semantically_validated);
      setValue(`configuration.form_error`, integration?.configuration?.form_error);
      setValue(`configuration.op_error`, integration?.configuration?.op_error);
      setValue(`configuration.op_form_error`, integration?.configuration?.op_form_error);
      setValue(`configuration.typeahead`, integration?.configuration?.typeahead);
    };

    fillForm();
  }, [append, integration, itemsAppend, setValue]);

  return (
    <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      <Stack sx={{ flexDirection: "column" }} spacing={2}>
        <Controller
          name="authorization.name"
          control={control}
          rules={{ required: "Name is required" }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              margin="normal"
              error={Boolean(fieldState.error)}
              id="authorization.name"
              label="Name"
              type="text"
              autoFocus
              helperText={fieldState.error?.message || "A name to help you organize your many integrations"}
              data-cy="authorization-name"
              onChange={(e) => onChange(e, field)}
            />
          )}
        />
        <FormComponent.SubHeader>Authentication</FormComponent.SubHeader>
        <Controller
          name="authorization.email"
          control={control}
          rules={{
            required: "Email is required",
            validate: validateEmail,
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              margin="normal"
              error={Boolean(fieldState.error)}
              id="authorization.email"
              label="Email"
              type="email"
              autoComplete="username"
              helperText={fieldState.error?.message}
              data-cy="authorization-email"
              onChange={(e) => onChange(e, field)}
            />
          )}
        />
        <Controller
          name="authorization.password"
          control={control}
          rules={{ required: "Password is required" }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              margin="normal"
              error={Boolean(fieldState.error)}
              id="authorization.password"
              label="Password"
              type="password"
              autoComplete="current-password"
              helperText={fieldState.error?.message}
              data-cy="authorization-password"
              onChange={(e) => onChange(e, field)}
            />
          )}
        />
        <FormComponent.SubHeader>Configuration</FormComponent.SubHeader>
        <Controller
          name="configuration.client_validated"
          control={control}
          rules={{ validate: lettersExceptAI }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              margin="normal"
              error={Boolean(fieldState.error)}
              name="configuration.client_validated"
              label="Client validated"
              type="text"
              helperText={fieldState.error?.message || "Validated on the client side"}
              data-cy="configuration-client-validated"
            />
          )}
        />

        <Controller
          name="configuration.schema_validated"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              margin="normal"
              error={Boolean(fieldState.error)}
              name="configuration.schema_validated"
              label="Schema validated"
              type="text"
              helperText={fieldState.error?.message || "Validated on the server by the JSON schema."}
              data-cy="configuration-schema-validated"
            />
          )}
        />

        <Controller
          name="configuration.denied"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              margin="normal"
              error={Boolean(fieldState.error)}
              name="configuration.denied"
              label="Schema denied"
              type="text"
              helperText={fieldState.error?.message || "Not permitted by the JSON schema."}
              data-cy="configuration-denied"
            />
          )}
        />

        <Controller
          name="configuration.semantically_validated"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              margin="normal"
              error={Boolean(fieldState.error)}
              name="configuration.semantically_validated"
              label="Semantically validated"
              type="text"
              helperText={fieldState.error?.message || "Validated on the server by the integration's validator."}
              data-cy="configuration-semantically-validated"
            />
          )}
        />
        <Controller
          name="configuration.form_error"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              margin="normal"
              error={Boolean(fieldState.error)}
              name="configuration.form_error"
              label="Form error"
              type="text"
              helperText={fieldState.error?.message || "Validated on the server, and the error applies to the form."}
              data-cy="configuration-form-error"
            />
          )}
        />

        <Controller
          name="configuration.op_error"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              margin="normal"
              error={Boolean(fieldState.error)}
              name="configuration.op_error"
              label="Operational error"
              type="text"
              helperText={fieldState.error?.message || "Simulates an error that occurs after save."}
              data-cy="configuration-op-error"
            />
          )}
        />

        <Controller
          name="configuration.op_form_error"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              margin="normal"
              error={Boolean(fieldState.error)}
              name="configuration.op_form_error"
              label="Operational form error"
              type="text"
              helperText={
                fieldState.error?.message || "Simulates an error that occurs after save, and it applies to the form."
              }
              data-cy="configuration-op-form-error"
            />
          )}
        />
        <Controller
          control={control}
          name="configuration.typeahead"
          render={({ field: { onChange, ...rest }, fieldState }) => {
            return (
              <Autocomplete
                disablePortal
                id="Typeahead"
                data-cy="Typeahead"
                options={months}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Typeahead"
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message || "This typeahead was statically populated when the form was loaded."
                    }
                  />
                )}
                onChange={(e, data) => onChange(data)}
                {...rest}
              />
            );
          }}
        />

        <FormComponent.SubHeader>Array Declared to GraphQL</FormComponent.SubHeader>
        {/* this should be checked with further info. ArrayItems returns any[] which is not allowed by ts */
        /* @ts-ignore */}
        <ArrayItems fields={fields}>
          {({ name, index }: { name: Record<"id", string>; index: number }) => (
            <Grid container sx={{ flexDirection: "column" }} key={`filters${index}`}>
              <Grid container sx={{ flexDirection: "row" }}>
                <Controller
                  control={control}
                  name={`filters[${index}].configuration.name`}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      margin="normal"
                      error={Boolean(
                        formState.errors?.filters &&
                        (formState.errors.filters as any)[index] &&
                        (formState.errors.filters as any)[index]?.configuration?.name
                      )}
                      name={`filters[${index}].configuration.name`}
                      label="Name"
                      type="text"
                      helperText={
                        formState.errors?.filters &&
                        (formState.errors.filters as any)[index] &&
                        (formState.errors.filters as any)[index]?.configuration?.name?.message
                      }
                      data-cy={`filters[${index}]-configuration-name`}
                    />
                  )}
                />
                <Grid size={8}>
                  <Controller
                    control={control}
                    name={`${name}.id`}
                    defaultValue={integration.id}
                    render={({ field }) => {
                      return <input {...field} type="hidden" />;
                    }}
                  />
                </Grid>
                <Grid container size={1} sx={{ justifyContent: "flex-end" }}>
                  <IconButton onClick={() => remove(index)}>
                    <TrashIcon />
                  </IconButton>
                </Grid>
              </Grid>
              <Grid container sx={{ flexDirection: "row" }}>
                <Grid container size={10} sx={{ flexDirection: "column" }}>
                  <Controller
                    control={control}
                    name={`filters[${index}].production`}
                    rules={{
                      validate: () => validateButtonRules(index),
                    }}
                    render={({ field }) => {
                      return (
                        <FormControlLabel
                          label="Production"
                          control={<Switch id={`production${index}`} checked={field.value} {...field} />}
                        />
                      );
                    }}
                  />
                  <Controller
                    control={control}
                    name={`filters[${index}].backfills`}
                    render={({ field }) => {
                      return (
                        <FormControlLabel
                          label="Backfills"
                          control={<Switch id={`backfills${index}`} checked={field.value} {...field} />}
                        />
                      );
                    }}
                  />
                  <Controller
                    control={control}
                    name={`filters[${index}].proofs`}
                    render={({ field }) => {
                      return (
                        <FormControlLabel
                          label="Tests"
                          control={<Switch id={`proofs${index}`} checked={field.value} {...field} />}
                          data-cy="proofs"
                        />
                      );
                    }}
                  />
                  <Controller
                    control={control}
                    name={`filters[${index}].lateUploads`}
                    render={({ field }) => {
                      return (
                        <FormControlLabel
                          label="Late Uploads"
                          control={<Switch id={`lateUploads${index}`} checked={field.value} {...field} />}
                        />
                      );
                    }}
                  />
                  <Controller
                    control={control}
                    name={`filters[${index}].beta`}
                    render={({ field }) => {
                      return (
                        <FormControlLabel
                          label="Beta"
                          control={<Switch id={`beta${index}`} checked={field.value} {...field} />}
                        />
                      );
                    }}
                  />
                  {checkError(index) ? (
                    <Typography sx={{ color: "accentDanger30.main" }}>At least one option must be selected</Typography>
                  ) : null}
                </Grid>
              </Grid>
            </Grid>
          )}
        </ArrayItems>

        <Stack sx={{ width: "20%" }}>
          <Button name="filters" onClick={() => append("filters")} data-cy="add-filter">
            Add Filter
          </Button>
        </Stack>

        <FormComponent.SubHeader>Array Inside a GenericScalar</FormComponent.SubHeader>
        {/* this should be checked with further info. ArrayItems returns any[] which is not allowed by ts */
        /* @ts-ignore */}
        {itemsField.map((item, index) => (
          <Grid container sx={{ flexDirection: "column" }} key={`configuration.items`[index]}>
            <Grid container sx={{ flexDirection: "row" }}>
              <Grid>
                <Controller
                  name={`configuration.items[${index}].fruit`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      margin="normal"
                      error={Boolean(fieldState.error)}
                      name={`configuration.items[${index}].fruit`}
                      label="Fruit"
                      type="text"
                      helperText={fieldState.error?.message}
                      data-cy={`configuration-items[${index}]-fruit`}
                    />
                  )}
                />
              </Grid>
              <Grid size={8} />
              <Grid container size={1} sx={{ justifyContent: "flex-end" }}>
                <IconButton onClick={() => itemsRemove(index)}>
                  <TrashIcon />
                </IconButton>
              </Grid>
            </Grid>
            <Grid size={3}>
              <Controller
                name={`configuration.items[${index}].color`}
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    error={Boolean(fieldState.error)}
                    name={`configuration.items[${index}].color`}
                    label="Color"
                    type="text"
                    helperText={fieldState.error?.message}
                    data-cy={`configuration-items[${index}]-color`}
                  />
                )}
              />
            </Grid>
          </Grid>
        ))}
        <Stack sx={{ width: "20%" }}>
          <Button name="items" onClick={() => itemsAppend("configuration.items")} data-cy="add-item">
            Add Item
          </Button>
        </Stack>
      </Stack>
      <Grid container sx={{ justifyContent: "flex-end", flexDirection: "row" }}>
        <StatefulButton
          id="saveIntegration"
          type="submit"
          data-cy="save-integration"
          loading={loading}
          startIcon={<FloppyDiskIcon width="12px" />}
        >
          Save
        </StatefulButton>
      </Grid>
    </form>
  );
};

const TEST_INTEGRATION = {
  integrationType: "test",
  title: "Test Integration",
  short_title: "Test",
  test: true,
  description: <p>Test an integration, without OAuth and not shared across accounts.</p>,
  image: "/img/integrations/test.png",
  oauth: false,
  clazz: TestIntegration,
  editor: TestIntegrationEditor,
};

export { TEST_INTEGRATION };
